import { useCallback } from 'react';
import { PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js';
import { usePrivy } from '@privy-io/react-auth';
import { useWallets as usePrivySolanaWallets } from '@privy-io/react-auth/solana';
import { useWallet as useAdapterWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';

export type WalletSource = 'privy' | 'native';

export interface UnifiedWallet {
  source: WalletSource | null;
  publicKey: PublicKey | null;
  isConnected: boolean;
  isReady: boolean;
  /**
   * True when the user has a Privy session but no embedded Solana wallet on
   * their account (e.g. they signed in via wallet method on a brand-new Privy
   * account). Sunset never creates wallets — `embeddedWallets.solana.createOnLogin: 'off'`
   * — so this can only fire for a genuinely empty account, never for an
   * existing Minemore user (who always has an embedded wallet from main-app
   * usage). Surfaced in LoginScreen with a clear sign-out / use-Connect-Wallet hint.
   */
  privyAuthedWithoutWallet: boolean;
  signTransaction:
    | (<T extends Transaction | VersionedTransaction>(tx: T) => Promise<T>)
    | null;
  loginWithPrivy: () => void;
  connectNativeWallet: () => void;
  disconnect: () => Promise<void>;
}

function deserializeLike<T extends Transaction | VersionedTransaction>(
  template: T,
  bytes: Uint8Array
): T {
  if (template instanceof VersionedTransaction) {
    return VersionedTransaction.deserialize(bytes) as T;
  }
  return Transaction.from(bytes) as T;
}

export function useUnifiedWallet(): UnifiedWallet {
  const privy = usePrivy();
  const { wallets: privyWallets } = usePrivySolanaWallets();

  // Mirror the main app's wallet selection: every selection site in
  // src/components reads `walletClientType === 'privy'` to grab the EMBEDDED
  // wallet specifically, ignoring externally-linked wallets like a Phantom
  // the user happened to link to their Privy account during a wallet-method
  // login. Without this filter, sunset would surface the Phantom address while
  // minemore.app shows the embedded address — different addresses for the
  // same user. See Dashboard.tsx:130-135, MotherlodeAnalytics.tsx:28-35,
  // EasyStartTopUp.tsx:28-33, EasyStartWelcome.tsx:38-43, etc.
  const privyEmbeddedWallet = (privy.user?.linkedAccounts ?? []).find(
    (a: { type?: string; chainType?: string; walletClientType?: string }) =>
      a.type === 'wallet' &&
      a.walletClientType === 'privy' &&
      a.chainType === 'solana'
  ) as { address?: string } | undefined;
  const privyEmbeddedAddress = privyEmbeddedWallet?.address ?? null;

  // For signing, look up the live useWallets() entry whose address matches
  // the embedded one. If useWallets() is still hydrating, we'll fall through
  // to wallets[0]; if even that's empty, signing is unavailable until Privy
  // finishes connecting — but the UI will already show the user as connected
  // (their embedded wallet exists per linkedAccounts), which is far less
  // confusing than the prior false-negative.
  const privySigningWallet =
    (privyEmbeddedAddress
      ? privyWallets.find((w) => w.address === privyEmbeddedAddress)
      : privyWallets[0]) ?? null;

  const adapter = useAdapterWallet();
  const { setVisible: openWalletModal } = useWalletModal();

  const privyConnected = privy.authenticated && !!privyEmbeddedAddress;
  const nativeConnected = adapter.connected && !!adapter.publicKey;
  const privyAuthedWithoutWallet =
    privy.authenticated && privy.ready && !privyEmbeddedAddress;

  // Resolution: Privy is authoritative whenever a Privy session has an
  // embedded wallet — that's the address the user mined with on minemore.app
  // and the one that owns their subaccounts. Native is a fallback path for
  // users with no Privy session (e.g. they exported their Privy key into
  // Phantom and don't want to sign back in via Privy). No localStorage
  // stickiness — past `active-source` flag was the source of the priority
  // confusion where a previous native connect would shadow Privy on later
  // visits.
  const resolvedSource: WalletSource | null = (() => {
    if (privyConnected) return 'privy';
    if (nativeConnected) return 'native';
    return null;
  })();

  const publicKey: PublicKey | null = (() => {
    if (resolvedSource === 'privy' && privyEmbeddedAddress) {
      try {
        return new PublicKey(privyEmbeddedAddress);
      } catch {
        return null;
      }
    }
    if (resolvedSource === 'native') return adapter.publicKey ?? null;
    return null;
  })();

  const signTransaction = (() => {
    if (resolvedSource === 'privy' && privySigningWallet) {
      return async <T extends Transaction | VersionedTransaction>(tx: T) => {
        const bytes = tx.serialize({ requireAllSignatures: false } as never) as Uint8Array;
        const result = await privySigningWallet.signTransaction({ transaction: bytes });
        return deserializeLike(tx, result.signedTransaction);
      };
    }
    if (resolvedSource === 'native' && adapter.signTransaction) {
      return adapter.signTransaction.bind(adapter) as <
        T extends Transaction | VersionedTransaction
      >(
        tx: T
      ) => Promise<T>;
    }
    return null;
  })();

  const loginWithPrivy = useCallback(() => {
    privy.login();
  }, [privy]);

  const connectNativeWallet = useCallback(() => {
    openWalletModal(true);
  }, [openWalletModal]);

  const disconnect = useCallback(async () => {
    // Check the underlying states, not resolvedSource — when a user is
    // Privy-authed without an embedded wallet (privyAuthedWithoutWallet),
    // resolvedSource is null and the LoginScreen logout button would be a
    // no-op, trapping the user on the error screen.
    if (privy.authenticated) {
      await privy.logout();
    }
    if (adapter.connected) {
      await adapter.disconnect();
    }
  }, [privy, adapter]);

  return {
    source: resolvedSource,
    publicKey,
    isConnected: !!publicKey,
    isReady: privy.ready,
    privyAuthedWithoutWallet,
    signTransaction,
    loginWithPrivy,
    connectNativeWallet,
    disconnect,
  };
}
